const configuredApiBaseUrl = (import.meta.env.VITE_API_BASE_URL || '').trim().replace(/\/$/, '');
const API_BASE_URL = import.meta.env.DEV ? '/api/v1' : configuredApiBaseUrl;

function assertApiBaseUrlConfigured() {
  if (API_BASE_URL) {
    return;
  }

  throw new ApiError('Frontend API base URL is not configured. Set VITE_API_BASE_URL before using the deployed app.', 0, null);
}

export function buildApiUrl(path) {
  assertApiBaseUrlConfigured();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

class ApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

export function getDisplayErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
  if (!error) {
    return fallback;
  }

  const status = error instanceof ApiError ? error.status : undefined;
  const message = typeof error?.message === 'string' ? error.message : '';

  if (/no active session|session expired/i.test(message)) {
    return 'Your session expired or is no longer active. Sign in again and retry.';
  }

  if (status === 401) {
    return message || 'Your session expired or is no longer active. Sign in again and retry.';
  }

  if (status === 403) {
    return 'You do not have permission to do that in this workspace.';
  }

  if (status === 404) {
    return 'The requested workspace data could not be found.';
  }

  if (status && status >= 500) {
    return 'Something went wrong on the server. Please try again in a moment.';
  }

  if (/failed to fetch|networkerror|load failed|network request failed/i.test(message)) {
    return "We couldn't reach the server. Check that the backend is running and try again.";
  }

  if (/<!doctype html>|<html/i.test(message)) {
    return 'Something went wrong on the server. Please try again in a moment.';
  }

  return message || fallback;
}

export async function apiGet(path) {
  return apiRequest(path);
}

export async function apiPost(path, body) {
  return apiRequest(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined
  });
}

export async function apiPatch(path, body) {
  return apiRequest(path, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
}

export async function apiPostForm(path, body) {
  return apiRequest(path, {
    method: 'POST',
    body,
    isFormData: true
  });
}

export async function apiDelete(path) {
  return apiRequest(path, {
    method: 'DELETE'
  });
}

async function apiRequest(path, options = {}) {
  assertApiBaseUrlConfigured();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  let response;
  const { isFormData = false, ...requestOptions } = options;

  try {
    response = await fetch(`${API_BASE_URL}${normalizedPath}`, {
      method: 'GET',
      headers: isFormData
        ? undefined
        : {
            'Content-Type': 'application/json'
          },
      credentials: 'include',
      ...requestOptions
    });
  } catch (networkError) {
    throw new ApiError(getDisplayErrorMessage(networkError), 0, null);
  }

  const responseText = await response.text();
  const parsedPayload = responseText ? safeJsonParse(responseText) : null;

  if (!response.ok) {
    const rawMessage =
      parsedPayload && typeof parsedPayload === 'object' && 'message' in parsedPayload && parsedPayload.message
        ? parsedPayload.message
        : responseText || `Request failed with status ${response.status}`;

    throw new ApiError(
      getDisplayErrorMessage(new ApiError(String(rawMessage), response.status, parsedPayload)),
      response.status,
      parsedPayload
    );
  }

  return parsedPayload;
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

export const apiFetcher = (path) => apiGet(path);
export { API_BASE_URL, ApiError };
